import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { useAuthStore } from '../store/useAuthStore';
import '../styles/Auth.css';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const FACEBOOK_APP_ID = process.env.REACT_APP_FACEBOOK_APP_ID || '';
const INSTAGRAM_CLIENT_ID = process.env.REACT_APP_INSTAGRAM_CLIENT_ID || '';
const API_BASE_URL = process.env.REACT_APP_API_URL;

/**
 * Validate password strength: letters + numbers + symbols
 */
function validatePassword(password) {
  const hasLetters = /[a-zA-Z]/.test(password);
  const hasNumbers = /[0-9]/.test(password);
  const hasSymbols = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password);
  const isLongEnough = password.length >= 8;

  return {
    isValid: hasLetters && hasNumbers && hasSymbols && isLongEnough,
    hasLetters,
    hasNumbers,
    hasSymbols,
    isLongEnough,
  };
}

/**
 * Decode Google JWT token to extract user info
 */
function decodeGoogleToken(token) {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const decoded = JSON.parse(atob(parts[1]));
    return {
      email: decoded.email,
      name: decoded.name,
      picture: decoded.picture,
    };
  } catch (error) {
    console.error('Failed to decode Google token:', error);
    return null;
  }
}

function RegisterContent() {
  const navigate = useNavigate();
  const register = useAuthStore((state) => state.register);
  const setToken = useAuthStore((state) => state.setToken);
  const setUser = useAuthStore((state) => state.setUser);
  const isLoading = useAuthStore((state) => state.isLoading);
  const error = useAuthStore((state) => state.error);
  const setError = useAuthStore((state) => state.setError);
  const setLoading = useAuthStore((state) => state.setLoading);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    gender: '',
  });

  const [passwordStrength, setPasswordStrength] = useState({
    isValid: false,
    hasLetters: false,
    hasNumbers: false,
    hasSymbols: false,
    isLongEnough: false,
  });

  const [registrationStep, setRegistrationStep] = useState('form'); // 'form' or 'verify'
  const [pendingEmail, setPendingEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationError, setVerificationError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Validate password strength as user types
    if (name === 'password') {
      setPasswordStrength(validatePassword(value));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setVerificationError('');

    // Validate password strength
    if (!passwordStrength.isValid) {
      setError('Password must contain letters, numbers, symbols, and be at least 8 characters');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setLoading(true);

      // Send verification code to email
      const response = await fetch(`${API_BASE_URL}/api/auth/send-verification-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          name: formData.name,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Failed to send verification code');
      }

      // Move to verification step
      setPendingEmail(formData.email);
      setRegistrationStep('verify');
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setVerificationError('');

    if (!verificationCode.trim()) {
      setVerificationError('Please enter the verification code');
      return;
    }

    try {
      setLoading(true);

      // Register user with verified email
      await register(
        formData.email,
        formData.password,
        formData.name,
        formData.phone,
        formData.gender,
        null, // country will be determined by IP geolocation on backend
        verificationCode // Pass verification code
      );

      navigate('/');
    } catch (err) {
      console.error('Verification error:', err);
      setVerificationError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      setLoading(true);
      setError(null);

      // Decode the Google token to get email and name
      const googleData = decodeGoogleToken(credentialResponse.credential);
      if (!googleData || !googleData.email) {
        throw new Error('Failed to decode Google token');
      }

      const response = await fetch(`${API_BASE_URL}/api/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          googleToken: credentialResponse.credential,
          email: googleData.email,
          name: googleData.name || formData.name || '',
          phone: formData.phone,
          gender: formData.gender,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Google authentication failed');
      }

      setToken(data.token);
      setUser(data.user);
      navigate('/');
    } catch (err) {
      console.error('Google OAuth error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Load Facebook SDK
  useEffect(() => {
    if (FACEBOOK_APP_ID && !window.FB) {
      window.fbAsyncInit = function() {
        window.FB.init({
          appId: FACEBOOK_APP_ID,
          cookie: true,
          xfbml: true,
          version: 'v18.0'
        });
      };

      (function(d, s, id) {
        var js, fjs = d.getElementsByTagName(s)[0];
        if (d.getElementById(id)) return;
        js = d.createElement(s); js.id = id;
        js.src = "https://connect.facebook.net/en_US/sdk.js";
        fjs.parentNode.insertBefore(js, fjs);
      }(document, 'script', 'facebook-jssdk'));
    }
  }, []);

  const handleInstagramLogin = async () => {
    try {
      setLoading(true);
      setError(null);

      // Instagram Basic Display API - redirect to Instagram authorization
      const redirectUri = `${window.location.origin}/auth/instagram/callback`;
      const instagramAuthUrl = `https://api.instagram.com/oauth/authorize?client_id=${INSTAGRAM_CLIENT_ID}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=user_profile,user_media&response_type=code`;
      
      if (!INSTAGRAM_CLIENT_ID) {
        throw new Error('Instagram login is not configured. Please contact support.');
      }

      // Store current page for redirect after auth
      sessionStorage.setItem('instagram_redirect', window.location.pathname);
      window.location.href = instagramAuthUrl;
    } catch (err) {
      console.error('Instagram login error:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const handleFacebookLogin = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!FACEBOOK_APP_ID || !window.FB) {
        throw new Error('Facebook login is not configured. Please contact support.');
      }

      window.FB.login(async (fbResponse) => {
        if (fbResponse.authResponse) {
          try {
            // Get user info from Facebook
            window.FB.api('/me', { fields: 'name,email' }, async (userInfo) => {
              if (userInfo.error) {
                throw new Error(userInfo.error.message || 'Failed to get Facebook user info');
              }

              const response = await fetch(`${API_BASE_URL}/api/auth/facebook`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  facebookToken: fbResponse.authResponse.accessToken,
                  email: userInfo.email || '',
                  name: userInfo.name || '',
                  phone: formData.phone,
                  gender: formData.gender,
                }),
              });

              const data = await response.json();

              if (!response.ok) {
                throw new Error(data.error || data.message || 'Facebook authentication failed');
              }

              setToken(data.token);
              setUser(data.user);
              navigate('/');
            });
          } catch (err) {
            console.error('Facebook OAuth error:', err);
            setError(err.message);
            setLoading(false);
          }
        } else {
          setError('Facebook login was cancelled');
          setLoading(false);
        }
      }, { scope: 'email,public_profile' });
    } catch (err) {
      console.error('Facebook login error:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <h1>Create Account</h1>

        {error && <div className="auth-error">{error}</div>}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="name">Full Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="Enter your full name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="Enter your email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone">Phone Number *</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="Enter your phone number"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="gender">Gender *</label>
            <select
              id="gender"
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              required
            >
              <option value="">Select gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="password">Password (Must contain letters, numbers, and symbols)</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              placeholder="Create a strong password"
              minLength="8"
            />
            {formData.password && (
              <div className="password-strength-checker">
                <div className={`strength-item ${passwordStrength.hasLetters ? 'valid' : 'invalid'}`}>
                  {passwordStrength.hasLetters ? '✓' : '✗'} Letters
                </div>
                <div className={`strength-item ${passwordStrength.hasNumbers ? 'valid' : 'invalid'}`}>
                  {passwordStrength.hasNumbers ? '✓' : '✗'} Numbers
                </div>
                <div className={`strength-item ${passwordStrength.hasSymbols ? 'valid' : 'invalid'}`}>
                  {passwordStrength.hasSymbols ? '✓' : '✗'} Symbols (!@#$%^&*)
                </div>
                <div className={`strength-item ${passwordStrength.isLongEnough ? 'valid' : 'invalid'}`}>
                  {passwordStrength.isLongEnough ? '✓' : '✗'} 8+ Characters
                </div>
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              placeholder="Confirm your password"
              minLength="6"
            />
          </div>

          <button type="submit" className="btn-submit" disabled={isLoading || !passwordStrength.isValid}>
            {isLoading ? 'Sending Verification Code...' : 'Continue'}
          </button>
        </form>

        {registrationStep === 'form' && (
          <>
            <div className="auth-divider">
              <span>OR</span>
            </div>

            <div className="oauth-buttons">
              {GOOGLE_CLIENT_ID ? (
                <div className="google-login-wrapper">
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={() => setError('Google login failed')}
                    useOneTap={false}
                    size="large"
                    width="100%"
                  />
                </div>
              ) : (
                <p className="oauth-note">Google Sign-In not configured</p>
              )}

              {INSTAGRAM_CLIENT_ID ? (
                <button
                  type="button"
                  className="btn-instagram"
                  onClick={handleInstagramLogin}
                  disabled={isLoading}
                >
                  Sign up with Instagram
                </button>
              ) : null}

              {FACEBOOK_APP_ID ? (
                <button
                  type="button"
                  className="btn-facebook"
                  onClick={handleFacebookLogin}
                  disabled={isLoading}
                >
                  Sign up with Facebook
                </button>
              ) : null}
            </div>

            <p className="auth-link">
              Already have an account? <Link to="/login">Log In</Link>
            </p>
          </>
        )}
      </div>

      {registrationStep === 'verify' && (
        <div className="auth-container">
          <h2>Verify Your Email</h2>
          <p className="verify-message">
            We've sent a verification code to <strong>{pendingEmail}</strong>
          </p>

          {verificationError && <div className="auth-error">{verificationError}</div>}

          <form onSubmit={handleVerifyCode} className="auth-form">
            <div className="form-group">
              <label htmlFor="verificationCode">Enter Verification Code</label>
              <input
                type="text"
                id="verificationCode"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder="Enter 6-digit code"
                maxLength="6"
                required
              />
            </div>

            <button type="submit" className="btn-submit" disabled={isLoading}>
              {isLoading ? 'Verifying...' : 'Verify & Create Account'}
            </button>
          </form>

          <button
            type="button"
            className="btn-link"
            onClick={() => {
              setRegistrationStep('form');
              setVerificationCode('');
            }}
            disabled={isLoading}
          >
            Back to Form
          </button>
        </div>
      )}
    </div>
  );
}

export default function Register() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <RegisterContent />
    </GoogleOAuthProvider>
  );
}
